/**
 * The core widgets' reads (Home spec 2026-09-23 D4): React Query over the
 * register's own endpoints. The page may use React Query (the app-wide
 * provider is in main.tsx); `OrdersPage` still may not.
 *
 * Query keys: these are Home's own (`['orders', ...]`) — `OrdersPage` uses
 * the hand-rolled `useOrders` lane with no React Query key, so there is no
 * collision to reconcile.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { transformOrder } from '@/hooks/useOrders'
import { apiClient } from '@/services/apiClient'
import type { Order } from '@/types/commerce'

export interface DayCount { day: string; count: number }

export interface HomeStatsWire {
  queue: {
    total: number
    not_started: number
    /** New enquiries widget (Home spec §5.2): the subset of `not_started`
     * whose `created_at` falls on today in the deployment's zone. */
    not_started_today: number
    by_status: Record<string, number>
    by_waiting_on: Record<string, number>
    /** SA-475 (spec §13 FF1): the open records whose latest quote version is
     * sent / countered — the list's own `quote_status=` clause. Optional on
     * the type so a payload without them reads as "no qualifier". */
    with_customers_sent?: number
    with_customers_countered?: number
    /** SA-1171 (spec §6.1): the sum of the latest version's amount over the
     * rows above, in the deployment's currency. Optional: an older server
     * sends none and the tile prints the count alone. */
    with_customers_amount_cents?: number
  }
  /** Closed this month (spec §5.9): closed quote-class records by outcome,
   * the deployment zone's current month. Optional on the type so an older
   * server reads as nothing closed. */
  outcomes_this_month?: Record<string, number>
  /** SA-1171 (spec §6.2): the last fourteen deployment-zone days, oldest
   * first, every day present. Optional: an older server draws no sparkline. */
  daily?: { received: DayCount[]; quotes_sent: DayCount[] }
  /** SA-1204 (spec §5): the request-to-first-quote measure for the deployment
   * zone's month; null without `process_engine`, absent on an older server. */
  first_quotes?: {
    target_minutes: number
    sent_this_month: number
    inside_target: number
    median_minutes: number | null
    waiting_longest: { order_id: string; order_number: string; received_at: string } | null
  } | null
}
export interface OrderPage { items: Order[]; total: number }

export const ordersStatsKey = ['orders', 'stats'] as const
export const ordersListKey = (query: string) => ['orders', 'list', query] as const

export function useOrderStats(): UseQueryResult<HomeStatsWire, Error> {
  return useQuery({
    queryKey: ordersStatsKey,
    queryFn: () => apiClient.get<HomeStatsWire>('/api/commerce/orders/stats'),
    staleTime: 30_000,
  })
}

/** One page of the register, transformed with the register's own row rule.
 * SA-1176: `enabled: false` sends nothing (the New enquiries widget's oldest-record
 * read is asked for only when the newest-five page cannot hold the oldest record);
 * the key and the body are unchanged, so every other caller keeps its bytes. */
export function useOrderPage(query: string, options: { enabled?: boolean } = {}): UseQueryResult<OrderPage, Error> {
  return useQuery({
    queryKey: ordersListKey(query),
    queryFn: async () => {
      const raw = await apiClient.get<{ items: Record<string, unknown>[]; total: number }>(`/api/commerce/orders?${query}`)
      return { items: (raw.items ?? []).map(transformOrder), total: raw.total ?? 0 }
    },
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  })
}
